import { Button, DateInput, Modal, TextInput, addToast } from '@octano/global-ui';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { toDateOnlyPayload } from '../../../../../../utils/dates';
import { format as formatPercentage } from '../../../../../../utils/percentage';
import { RateBasis, toDailyRate, toMonthlyRate } from '../../shared/rateConversion';
import {
  LateInterestRateItem,
  createLateInterestRate,
  updateLateInterestRate,
} from '../api';

const dictPrefix = 'financialPortal.settings.lateInterest';

interface FormValues {
  rate: string;
  graceDays: string;
  validFrom: Date | string | null;
}

interface Props {
  isOpen: boolean;
  rateBasis: RateBasis;
  item: LateInterestRateItem | null;
  onClose: () => void;
  onSaved: () => void;
}

// `raw` va tipado `any` por la convención del repo para los `validate` de
// react-hook-form: con varios campos el tipo del valor se ensancha a la unión.
//
// El `required` de react-hook-form sólo rechaza valores falsy y un espacio en
// blanco es truthy, así que se hace trim antes de convertir: sin él, `' '` se
// convertiría en `Number(' ') === 0` y se guardaría como 0.
function validateRange(
  min: number,
  max: number,
  message: string,
  integer: boolean,
) {
  return (raw: any) => {
    const trimmed = typeof raw === 'string' ? raw.trim() : raw;
    if (trimmed === '' || trimmed === null || trimmed === undefined) {
      return message;
    }
    const value = Number(trimmed);
    if (Number.isNaN(value) || value < min || value > max) {
      return message;
    }
    if (integer && !Number.isInteger(value)) {
      return message;
    }
    return true;
  };
}

export function ConfigModal({
  isOpen,
  rateBasis,
  item,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const { control, handleSubmit, formState, reset } = useForm<FormValues>({
    defaultValues: { rate: '', graceDays: '', validFrom: null },
  });

  const isMonthly = rateBasis === 'monthly';

  // El modal no se desmonta: sin este reset el formulario conserva lo anterior
  // al cerrar y reabrir, y al pasar de editar a crear.
  useEffect(() => {
    if (item) {
      reset({
        // OJO: no `item.rate`. `item.rate` está en la base con que la fila se
        // guardó (`item.rateBasis`), pero la etiqueta del campo — arriba —
        // usa la base ACTUAL del tenant (`isMonthly`, de `rateBasis`). Si el
        // setting cambió entre que la fila se creó y se edita, ambas bases
        // difieren: mostrar `item.rate` bajo la etiqueta equivocada, y al
        // guardar sin tocarlo el service vuelve a sellar `rateBasis` con la
        // base actual, reinterpretando en silencio 3 % mensual como 3 %
        // diario. La API ya manda las dos tasas derivadas (`monthlyRate` /
        // `dailyRate`); usar la que corresponde a `isMonthly` mantiene el
        // número siempre expresado en la base que la etiqueta dice y con la
        // que se volverá a guardar.
        rate: String(isMonthly ? item.monthlyRate : item.dailyRate),
        graceDays: String(item.graceDays),
        validFrom: item.validFrom,
      });
    } else {
      reset({ rate: '', graceDays: '', validFrom: null });
    }
  }, [isOpen, item, isMonthly, reset]);

  const values = useWatch({ control });

  // El campo derivado: si se ingresa la mensual se muestra la diaria y al
  // revés. Vacío mientras no haya un número válido, que es el "Calculando…"
  // del XD. Mismo formateador que la tabla (utils/percentage): dos decimales
  // en ambos sentidos — solo la vista se redondea, `value` conserva la
  // precisión completa y es lo que se envía al guardar.
  const derived = useMemo(() => {
    const entered = Number(String(values.rate ?? '').trim());
    if (!String(values.rate ?? '').trim() || Number.isNaN(entered)) {
      return t(`${dictPrefix}.form.calculating`);
    }
    const value = isMonthly
      ? toDailyRate(entered, 'monthly')
      : toMonthlyRate(entered, 'daily');
    return formatPercentage(value, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [isMonthly, t, values.rate]);

  const onSubmit = useCallback(
    async (formValues: FormValues) => {
      const payload = {
        rate: Number(String(formValues.rate).trim()),
        graceDays: Number(String(formValues.graceDays).trim()),
        validFrom: toDateOnlyPayload(formValues.validFrom),
      };

      const { error } = item
        ? await updateLateInterestRate(item.id, payload)
        : await createLateInterestRate(payload);

      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.saveError`),
        });
        return;
      }
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${dictPrefix}.saveSuccess`),
      });
      onSaved();
    },
    [item, onSaved, t],
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md">
      <h1 className="text-dark fs-22 mb-4 text-center">
        {t(`${dictPrefix}.form.title`)}
      </h1>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xs={12} md={6}>
            <TextInput
              name="rate"
              label={t(
                `${dictPrefix}.form.${isMonthly ? 'monthlyRate' : 'dailyRate'}`,
              )}
              control={control}
              rules={{
                required: t(`${dictPrefix}.form.required`) as string,
                validate: validateRange(
                  0,
                  100,
                  t(`${dictPrefix}.form.rateRange`) as string,
                  false,
                ),
              }}
            />
          </Col>
          <Col xs={12} md={6}>
            <TextInput
              name="graceDays"
              label={t(`${dictPrefix}.form.graceDays`)}
              control={control}
              rules={{
                required: t(`${dictPrefix}.form.required`) as string,
                validate: validateRange(
                  0,
                  365,
                  t(`${dictPrefix}.form.graceDaysRange`) as string,
                  true,
                ),
              }}
            />
          </Col>
          <Col xs={12} md={6}>
            <DateInput
              name="validFrom"
              label={t(`${dictPrefix}.form.validFrom`)}
              control={control}
              // Una fecha pasada reescribiría interés ya devengado; el servidor
              // la rechaza igualmente con VALID_FROM_IN_THE_PAST.
              minDate={new Date()}
              rules={{ required: t(`${dictPrefix}.form.required`) as string }}
            />
          </Col>
          <Col xs={12} md={6}>
            {/* Derivado, nunca se envía: sin `control`, TextInput pinta el
                `value` que recibe. */}
            <TextInput
              name="derivedRate"
              label={t(
                `${dictPrefix}.form.${isMonthly ? 'dailyRate' : 'monthlyRate'}`,
              )}
              value={derived}
              readOnly
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={12} md={6}>
            <Button
              outlined
              fullwidth
              text={t(`${dictPrefix}.form.cancel`)}
              onClick={onClose}
            />
          </Col>
          <Col xs={12} md={6}>
            <Button
              type="submit"
              fullwidth
              loading={formState.isSubmitting}
              text={t(`${dictPrefix}.form.save`)}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
